import React from 'react'

import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Stack from '@mui/material/Stack'
import type { SxProps, Theme } from '@mui/material/styles'

import { useContainerDimensions } from '../../hooks/useContainerDimensions'

import { Confetti } from './Confetti'

interface ConnectionCardProps {
  centerIconBgColor?: string
  dotBgColor?: string
  hasBigDots?: boolean
  hasConfetti?: boolean
  icon: React.ReactNode
  leftIcon: React.ReactNode
  rightIcon: React.ReactNode
  sx?: SxProps<Theme>
}

interface DotProps {
  bgcolor?: string
  size?: number
}

const Dot = ({ bgcolor, size = 4 }: DotProps) => (
  <Box sx={{ borderRadius: '4px', bgcolor, height: size, width: size }} />
)

const ConnectionCard = ({
  centerIconBgColor = 'text.secondary',
  dotBgColor = 'text.secondary',
  hasBigDots = false,
  hasConfetti = false,
  icon,
  leftIcon,
  rightIcon,
  sx,
}: ConnectionCardProps) => {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const { height, width } = useContainerDimensions(containerRef)

  const dotSize = hasBigDots ? 6 : 4

  return (
    <React.Fragment>
      {hasConfetti && <Confetti height={height} shouldRun={true} width={width} />}
      <Box ref={containerRef} sx={{ p: 16, ...sx }}>
        <Card sx={{ py: 16 }}>
          <Box
            sx={{
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <Box
              sx={{
                bgcolor: 'neutral.lighter',
                borderRadius: '24px',
                gridArea: '1 / 1',
                height: 40,
                width: 240,
              }}
            />
            <Stack
              sx={{
                alignItems: 'center',
                flexDirection: 'row',
                gridArea: '1 / 1',
                height: 64,
                justifyContent: 'space-between',
                width: 204,
              }}
            >
              {leftIcon}
              <Stack sx={{ alignItems: 'center', flexDirection: 'row', gap: 4 }}>
                <Dot bgcolor={dotBgColor} />
                <Dot bgcolor={dotBgColor} size={dotSize} />
                <Stack
                  sx={{
                    alignItems: 'center',
                    bgcolor: centerIconBgColor,
                    borderRadius: '50%',
                    height: 28,
                    justifyContent: 'center',
                    width: 28,
                  }}
                >
                  {icon}
                </Stack>
                <Dot bgcolor={dotBgColor} size={dotSize} />
                <Dot bgcolor={dotBgColor} />
              </Stack>
              {rightIcon}
            </Stack>
          </Box>
        </Card>
      </Box>
    </React.Fragment>
  )
}

export default ConnectionCard
